import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
// import Button from '@mui/material/Button'; // Không dùng thì có thể bỏ
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
// api
import { thongbaoApiSerice } from 'src/api/home-api';
// types
import { ThongBao } from 'src/types/notification';
// service
// eslint-disable-next-line import/no-named-as-default
import signalRService from 'src/components/settings/drawer/signalR.service'; 

import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

type TabValue = 'all' | 'unread' | 'archived';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const smUp = useResponsive('up', 'sm');

  const [currentTab, setCurrentTab] = useState<TabValue>('all');
  const [notifications, setNotifications] = useState<ThongBao[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await thongbaoApiSerice.Notifications({
        skip: 0,
        take: 100,
        Status: 1,
        orderBy: ["NgayKhoiTao desc"]
      });

      if (response.messCode === 1 && response.items) {
        setNotifications(response.items);
        const unread = response.items.filter((item) => !item.seen).length;
        setUnreadCount(unread);
      } else {
        setError(response.message || 'Không thể tải thông báo');
      }
    } catch (err: any) {
      setError(err.message || 'Đã xảy ra lỗi khi tải thông báo');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await thongbaoApiSerice.Notifications({ skip: 0, take: 100, Status: 1 });
      if (response.messCode === 1 && response.items) {
          const unread = response.items.filter((item) => !item.seen).length;
          setUnreadCount(unread);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Load số lượng chưa đọc ban đầu
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Lắng nghe thông báo real-time từ SignalR
  useEffect(() => {
    const unsubscribe = signalRService.onNotification(() => {
      console.log('Popover received notification. Updating count and refetching list...');
      
      // Cập nhật số đếm ngay lập tức để Badge hiển thị liền
      setUnreadCount(prevCount => prevCount + 1);

      // Nếu drawer đang mở, tải lại danh sách để hiển thị thông báo mới
      if (drawer.value) {
        fetchNotifications();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [drawer.value, fetchNotifications]);

  // Load danh sách chi tiết khi mở drawer
  useEffect(() => {
    if (drawer.value) {
      fetchNotifications();
    }
  }, [drawer.value, fetchNotifications]);

  const TABS = [
    { value: 'all' as TabValue, label: 'Tất cả', count: notifications.length },
    { value: 'unread' as TabValue, label: 'Chưa xem', count: unreadCount },
    { value: 'archived' as TabValue, label: 'Đã xem', count: notifications.length - unreadCount },
  ];

  const filteredNotifications = notifications.filter((notification) => {
    if (currentTab === 'unread') return !notification.seen;
    if (currentTab === 'archived') return notification.seen;
    return true; // 'all'
  });

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);
  }, []);

  // --- XỬ LÝ ĐỌC 1 THÔNG BÁO ---
  const handleMarkAsRead = useCallback(async (id: number) => {
    // 1. Optimistic Update (Cập nhật giao diện trước)
    setNotifications((prev) =>
      prev.map((notification) => 
        notification.id === id ? { ...notification, seen: true } : notification
      )
    );
    
    // Giảm unread count nếu item đó chưa seen
    const targetItem = notifications.find(n => n.id === id);
    if (targetItem && !targetItem.seen) {
        setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
    }

    try {
      // 2. Gọi API Background
      await thongbaoApiSerice.seenThongBao(id);
    } catch (err) {
      console.error('Lỗi khi đánh dấu đã đọc:', err);
      // Nếu lỗi, có thể revert lại state ở đây nếu cần thiết
    }
  }, [notifications]);

  // --- XỬ LÝ ĐỌC TẤT CẢ ---
  const handleMarkAllAsRead = useCallback(async () => {
    // 1. Optimistic Update
    const previousNotifications = [...notifications]; // Backup state cũ
    
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, seen: true }))
    );
    setUnreadCount(0);

    try {
      // 2. Gọi API
      await thongbaoApiSerice.seenThongBaoAll();
    } catch (err) {
      console.error('Error marking all as read:', err);
      // Revert lại nếu lỗi nghiêm trọng
      setNotifications(previousNotifications);
      fetchUnreadCount(); 
    }
  }, [notifications, fetchUnreadCount]);

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Thông báo
      </Typography>

      {!!unreadCount && (
        <Tooltip title="Đánh dấu tất cả đã đọc">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}> 
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={(tab.value === 'unread' && 'info') || 'default'}
            >
              {tab.count}
            </Label>
          }
          sx={{ '&:not(:last-of-type)': { mr: 3 } }}
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      {/* eslint-disable-next-line no-nested-ternary */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      // eslint-disable-next-line no-nested-ternary
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      ) : filteredNotifications.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 5 }}>
          <Iconify icon="solar:bell-off-bold-duotone" width={64} sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Không có thông báo nào
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {filteredNotifications.map((notification) => (
            <NotificationItem 
              key={notification.id} 
              notification={notification}
              onRead={() => handleMarkAsRead(notification.id)} 
            />
          ))}
        </List>
      )}
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}
        <Divider />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 2.5, pr: 1 }}>
          {renderTabs}
          <IconButton onClick={fetchNotifications}>
            <Iconify icon="solar:refresh-bold-duotone" />
          </IconButton>
        </Stack>
        <Divider />
        {renderList}
      </Drawer>
    </>
  );
}